import { useState } from "react";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Crown, CheckCircle, Loader2, Gift, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const SubscriptionCard = ({ subscription, subLoading, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoValid, setPromoValid] = useState(null);
  const [promoValidating, setPromoValidating] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoValidating(true);
    try {
      const res = await api.get(`/stripe/validate-promo/${promoCode.trim()}`);
      setPromoValid(res.data);
    } catch {
      setPromoValid(null);
      toast.error("Codice promo non valido");
    } finally {
      setPromoValidating(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await api.post("/stripe/create-checkout", {
        origin_url: origin,
        promo_code: promoCode.trim() || null,
      });
      if (res.data.url) {
        if (res.data.url.includes("subscription=activated")) {
          toast.success("Accesso attivato!");
          onRefresh();
        } else {
          window.location.href = res.data.url;
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella sottoscrizione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border border-border shadow-sm rounded-sm overflow-hidden">
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 text-white">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5" />
          <CardTitle className="font-heading text-lg text-white">Abbonamento Premium</CardTitle>
        </div>
      </div>
      <CardContent className="pt-4 space-y-4">
        {subLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : subscription?.is_active ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle className="w-3 h-3 mr-1" />
                {subscription.status === "trialing" ? "Prova Gratuita" : "Attivo"}
              </Badge>
            </div>
            <div className="space-y-2 text-sm">
              {subscription.trial_end && subscription.status === "trialing" && (
                <p className="text-muted-foreground">
                  Prova gratuita fino al:{" "}
                  <span className="font-medium text-foreground">
                    {new Date(subscription.trial_end).toLocaleDateString("it-IT", { day: "numeric", month: "long" })}
                  </span>
                </p>
              )}
              {subscription.current_period_end && (
                <p className="text-muted-foreground">
                  Prossimo rinnovo:{" "}
                  <span className="font-medium text-foreground">
                    {new Date(subscription.current_period_end).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                  </span>
                </p>
              )}
            </div>
            <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setShowCancelDialog(true)}>
              Cancella abbonamento
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold">{"\u20AC"}4,99</span>
                <span className="text-muted-foreground">/mese</span>
              </div>
              <ul className="space-y-2 text-sm">
                {["3 giorni di prova gratuita", "Accesso completo a tutte le funzionalit\u00e0", "Cancella quando vuoi"].map((text) => (
                  <li key={text} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Hai un codice promo?</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Inserisci codice"
                    value={promoCode}
                    onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoValid(null); }}
                    className="pl-9 uppercase"
                    data-testid="promo-code-input"
                  />
                </div>
                <Button variant="outline" onClick={handleValidatePromo} disabled={promoValidating || !promoCode.trim()} data-testid="validate-promo-btn">
                  {promoValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verifica"}
                </Button>
              </div>
              {promoValid && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  {promoValid.discount_percent === 100 ? "Accesso gratuito!" : `Sconto ${promoValid.discount_percent}% applicato`}
                </p>
              )}
            </div>
            <Button className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700" onClick={handleSubscribe} disabled={loading} data-testid="subscribe-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
              {promoValid?.discount_percent === 100 ? "Attiva Accesso Gratuito" : "Inizia la prova gratuita"}
            </Button>
            <p className="text-xs text-center text-muted-foreground">Pagamento sicuro con Stripe. Cancella in qualsiasi momento.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
